import { animate, style, transition, trigger } from '@angular/animations';
import { KeyValue, KeyValuePipe, LowerCasePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { first, map, mergeMap, pairwise, tap } from 'rxjs/operators';
import { AdminService } from '../admin/admin.service';
import { HeaderServiceDirective } from '../header/header.service';
import { TransformFileAttachment } from '../kyloe-pipes.pipe';
import { RippleDirective } from '../ripple.directive';
import { DataToolsApps } from '../shared/DataToolsApps';
import { LoadingSnackbarComponent } from '../shared/loading-snackbar/loading-snackbar.component';
import { LoadingSnackbarService, SnackMessageNames } from '../shared/services/loading-snackbar.service';
import { UtilsService } from '../shared/services/utils.service';
import { SkeletonComponent } from '../shared/skeleton/skeleton.component';
import { DataRulesConfirmDialogComponent } from './data-rules-confirm-dialog/data-rules-confirm-dialog.component';
import { DataRulesDataService } from './data-rules-data.service';
import {
  ActionsOperators,
  AllPrimaryEntitiesNames,
  AllPrimaryEntitiesTypes,
  CriteriaData,
  DataRuleId,
  DefaultEntities,
  IDataRule,
  IDataRulesTasks,
  RuleData,
  TaskState
} from './data-rules-model';
import { ENRICH_CREDIT_EXHAUSTED_TOOLTIP, EnrichmentCreditService } from '../enrichment-settings/enrichment-credit.service';
import { ErrorModalComponent } from './error-modal/error-modal.component';
import { ErrorNotificationService } from './error-notification.service';
import { CustomOverlayForDirective, ICustomPositioningConfig } from './lib/custom-component-overlay.directive';
import { MetaDataService } from './meta-data.service';
import { ChainData, IRuleInChainData, RuleChainsDataService } from './rule/rule-chain/rule-chain-data.service';
import { RuleInChainScreenComponent } from './rule/rule-chain/rule-in-chain-screen/rule-in-chain-screen.component';
import { RuleScheduleComponent } from './rule/rule-schedule/rule-schedule.component';
import { ScheduleData, ScheduleDataService } from './rule/rule-schedule/schedule-data.service';
import { RuleComponent } from './rule/rule.component';
import { SocketManager } from './socket-manager';

export interface ISavedDataRuleEventData {
  unsavedId: DataRuleId;
  savedId: string;
}

@Component({
  selector: 'app-data-rules',
  templateUrl: './data-rules.component.html',
  styleUrls: ['./data-rules.component.scss'],
  imports: [
    NgClass,
    MatButtonToggleGroup,
    NgxSkeletonLoaderModule,
    MatButtonToggle,
    NgForOf,
    NgIf,
    KeyValuePipe,
    TransformFileAttachment,
    LowerCasePipe,
    RuleComponent,
    RuleScheduleComponent,
    CustomOverlayForDirective,
    RuleInChainScreenComponent,
    MatTooltip,
    LoadingSnackbarComponent,
    RippleDirective,
    SkeletonComponent
  ],
  animations: [
    trigger('transitionNewRowInOut', [
      transition(':enter', [
        style({ height: '0' }),
        animate('.2s ease-out', style({ height: '71px' })),
      ]),
      transition(':leave', [
        style({ height: '71px' }),
        animate('.2s ease-out', style({ height: '0' })),
      ]),
    ]),
  ]
})

// TODO: Would benefit a refactor, a lot of chunky code, especially ngOnInit

export class DataRulesComponent implements OnInit, OnDestroy {
  dataRules: IDataRule[] = [];
  coreEntities: { [key: string]: number } = {};
  fileAttachmentEntities: { [key: string]: number } = {};
  customObjectEntities: { [key: string]: number } = {};
  additionalEntities: { [key: string]: number } = {};
  protected selectedEntity: AllPrimaryEntitiesNames;
  newRuleButtonsDisabled = false;
  entityMap: any;
  entitySelectorsMap: any;
  currentRuleExamples: RuleData;
  tasksRunning = [];
  metaLoaded = false;
  rulesLoaded = false;
  ruleSaved = true;

  extraEntitiesExpanded = false;
  dataToolsApps = DataToolsApps;
  schedulingEnabled = false;
  amountOfScheduledRules: number;
  deletingSchedule = false;
  rulesThatInChains: IRuleInChainData[] = [];
  savedRuleEvent: Subject<ISavedDataRuleEventData> = new Subject<ISavedDataRuleEventData>();

  chainedRulesEnabled: boolean;

  enrichCreditsExhausted = false;
  readonly enrichCreditBlockedTooltip = ENRICH_CREDIT_EXHAUSTED_TOOLTIP;

  ruleInChainOverlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: -24,
    offsetX: -56
  };

  @ViewChildren(RuleComponent)
  rules: QueryList<RuleComponent>;

  routeParamEntity: AllPrimaryEntitiesNames;
  routeParamRuleIds: DataRuleId[];

  private _socketManager: SocketManager<IDataRulesTasks>;
  private coreOrder = ['Candidate', 'ClientContact', 'ClientCorporation', 'JobOrder', 'Opportunity', 'Placement', 'Lead'];

  constructor(public dataRulesDataService: DataRulesDataService,
              private metaDataService: MetaDataService,
              private errorNotificationService: ErrorNotificationService,
              private dialog: MatDialog,
              private adminService: AdminService,
              private headerService: HeaderServiceDirective,
              private route: ActivatedRoute,
              private router: Router,
              public loadingSnackbarService: LoadingSnackbarService,
              public ruleChainsDataService: RuleChainsDataService,
              public scheduleDataService: ScheduleDataService,
              public utilsService: UtilsService,
              private enrichmentCreditService: EnrichmentCreditService) {
    this._socketManager = new SocketManager<IDataRulesTasks>(this.socketTaskUpdateHandler.bind(this), this.socketErrorHandler.bind(this));
    this.errorHandler();
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + E to expand/collapse entity button rows
  // - Use Alt + Shift + N to Create a New Rule
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyE') {
        window.scrollTo(0, 0);
        this.extraEntitiesExpanded = !this.extraEntitiesExpanded;
      }
      if (event.code === 'KeyR') {
        this.addNewRule();
      }
    }
  }

  ngOnInit() {
    this.enrichmentCreditService.getCreditBalance().subscribe({
      next: balance => {
        this.enrichCreditsExhausted = balance.balance <= 0;
      }
    });

    this.ruleChainsDataService.usedRules.subscribe(usedRules => {
      this.rulesThatInChains = usedRules;
    });

    console.time('dr-startup');
    this.loadingSnackbarService.show(SnackMessageNames.META_LOADED);
    this.loadingSnackbarService.show(SnackMessageNames.RULES_LOADED);
    const chains$ = this.ruleChainsDataService.initialize(true);
    const schedules$ = this.scheduleDataService.initialize(true);
    const rulesData$ = this.dataRulesDataService.initialize(true);
    let meta$ = this.metaDataService.getAllMeta();
    meta$ = meta$.pipe(tap(_ => {
      this.metaLoaded = true;
      this.loadingSnackbarService.hide(SnackMessageNames.META_LOADED);
      this.entityMap = this.metaDataService.getEntitiesLabelsMap();
      this.entitySelectorsMap = this.metaDataService.getEntitiesSelectorsMap();
    }));

    const firstOrder$ = forkJoin({meta: meta$, chains: chains$, schedules: schedules$});
    const secondOrder$ = forkJoin({
      // rulesData initialization requires metaData service to be initialized(getAllMeta()) before
      rulesData: rulesData$
    });

    const actions$ = firstOrder$.pipe(
      mergeMap(({...firstOrderJoins}) => {
        return secondOrder$.pipe(
          map(({...secondOrderJoins}) => {
            return {...firstOrderJoins, ...secondOrderJoins};
          })
        );
      }),
      tap(_ => {
        this.chainedRulesEnabled = this.adminService.checkAppEnabled(DataToolsApps.DATARULES_CHAINED_RULES);
        this.rulesLoaded = true;
        this.loadingSnackbarService.hide(SnackMessageNames.RULES_LOADED);
      })
    );

    actions$.subscribe(_ => {
        this.dataRulesDataService.availableEntities.forEach(entityType => {
          if (this.isCoreEntity(entityType))
            this.coreEntities[entityType] = 0;
          else if (this.isFileAttachmentEntity(entityType))
            this.fileAttachmentEntities[entityType] = 0;
          else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
            this.customObjectEntities[entityType] = 0;
          else if (this.metaDataService.isPrimaryEntity(entityType))
            this.additionalEntities[entityType] = 0;
        });

        this.dataRulesDataService.allRules.forEach(rule => {
          const entityType = rule.source?.config?.entity;
            if (typeof entityType !== 'undefined') {
              if (this.isCoreEntity(entityType))
                this.coreEntities[entityType]++;
              else if (this.isFileAttachmentEntity(entityType))
                this.fileAttachmentEntities[entityType]++;
              else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
                this.customObjectEntities[entityType]++;
              else if (this.metaDataService.isPrimaryEntity(entityType))
                this.additionalEntities[entityType]++;
            }
        });
        if (this.adminService.checkAppEnabled(this.dataToolsApps.DATARULES_SCHEDULING))
          this.populateScheduleList();
        console.timeEnd('dr-startup');
        this.route.queryParams.pipe(first()).subscribe(queryParams => this.onRouteChange(null, queryParams));
        this.route.queryParams.pipe(pairwise())
          .subscribe(([previousQueryParams, queryParams]) => this.onRouteChange(previousQueryParams, queryParams));
        this.savedRuleEvent.subscribe((eventData: ISavedDataRuleEventData) => {
          this.onRuleSaved(eventData);
        });
      },
      error => {
        this.errorNotificationService.error('Failed to load data', error);
      });
  }

  ngOnDestroy() {
    this._socketManager.closeAll();
  }

  getRuleInChainInfo(ruleId: DataRuleId): IRuleInChainData | null {
    if (this.utilsService.compareInternalDataRuleIds(ruleId, 0)) {
      return null;
    }
    const foundRuleInChain = this.rulesThatInChains.find(
      ruleInChain => this.utilsService.compareInternalDataRuleIds(ruleInChain.id, ruleId)
    );
    return typeof foundRuleInChain === 'undefined'
      ? null
      : foundRuleInChain;
  }

  isRuleInAChain(ruleId: DataRuleId): boolean {
    return !!this.getRuleInChainInfo(ruleId);
  }

  isRuleFirstInAChain(ruleId: DataRuleId): boolean {
    if (ruleId === 0) {
      return false;
    }
    return this.getRuleInChainInfo(ruleId)?.index === 0;
  }

  ruleSchedule(ruleId: string): ScheduleData | null {
    return this.scheduleDataService.getScheduleByRuleId(ruleId);
  }

  private populateScheduleList(): void {

    this.checkForMaxScheduledRulesExceeded();
    this.schedulingEnabled = true;
  }

  private populateDataRules(): void {
    if (!this.dataRulesDataService.isInitialisedOnce) {
      return;
    }
    // TODO: Fix - Cloned rules because components are changing the source object, rather than just the form controls
    this.dataRules = JSON.parse(JSON.stringify(
        this.dataRulesDataService.allRules.filter(rule => rule.source?.config?.entity === this.selectedEntity)
            .map(rule => {
              return {
                ruleData: rule,
                viewState: { expanded: false, dirty: false }
              };
            })));
    console.log(`KPDR: ${this.selectedEntity} Rules`, this.dataRules);
  }

  private checkForMaxScheduledRulesExceeded() {

    const validScheduledRules = this.scheduleDataService.allSchedules.filter(
      scheduledRule => !scheduledRule.isExpired
    );

    this.amountOfScheduledRules = validScheduledRules.length;

    if ((this.amountOfScheduledRules >= this.adminService.maxNumberOfAutomatedRules) && this.adminService.maxNumberOfAutomatedRules > 0) {
      this.headerService.maxNumberOfAutomatedRulesExceeded.emit(true);
    } else {
      this.headerService.maxNumberOfAutomatedRulesExceeded.emit(false);
    }

  }

  private socketTaskUpdateHandler(task: IDataRulesTasks): void {
    task.state = TaskState[task.state];
    if (!this.isActive(task.state)) {
      console.log(`task update: ${task.id} ${task.state}`, task);
      this._socketManager.close(task.id);
      const index = this.tasksRunning.findIndex(t => t.id === task.id);
      if (index > -1) {
        console.log(`remove task running: ${task.id} at ${index}`);
        this.tasksRunning.splice(index, 1);
      }
    }
  }

  private socketErrorHandler(error: any): void {
    console.log('socket error:', error);
  }

  private isActive(state: TaskState): boolean {
    return state === TaskState.RUNNING || state === TaskState.QUEUED || state === TaskState.STOPPING;
  }

  onEntitySelected(entity: AllPrimaryEntitiesNames): void {
    this.currentRuleExamples = null;

    this.selectedEntity = entity;
    this.extraEntitiesExpanded = !this.isCoreEntity(this.selectedEntity);
    this.newRuleButtonsDisabled = false;

    this.populateDataRules();
  }

  addNewRule(): void {
    const newRule = {ruleData: new RuleData(0, '', this.selectedEntity), viewState: {expanded: true, dirty: true}};
    newRule.ruleData.source.config.criteria = [new CriteriaData('', 'equals')];
    this.dataRules.push(newRule);
    this.newRuleButtonsDisabled = true;
    this.onRuleClicked(0);
  }

  deleteRule(index: number): void {
    const rule = this.dataRules[index].ruleData;
    const ruleInChain = this.getRuleInChainInfo(rule.id);

    let dialogTitle = 'Are you sure you want to delete this rule?';
    if (this.ruleSchedule(rule.id as string) && !!ruleInChain)
      dialogTitle = 'This rule is scheduled and in a chain, are you sure you want to delete it?';
    else if (this.ruleSchedule(rule.id as string) && !ruleInChain)
      dialogTitle = 'This rule is scheduled, are you sure you want to delete it?';
    else if (!this.ruleSchedule(rule.id as string) && !!ruleInChain)
      dialogTitle = 'This rule is in a chain, are you sure you want to delete it?';

    const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
      width: '45vw',
      height: 'auto',
      autoFocus: true,
      data: {
        titleIcon: 'bhi-delete-o',
        title: dialogTitle,
        messageIcon: 'bhi-search',
        message: rule.name
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data)
        return;

      this.dataRulesDataService.deleteRule(rule.id).subscribe(_ => {
        const entityType = rule.source?.config?.entity;
        if (typeof entityType !== 'undefined' && rule.id !== 0) {
          if (this.isCoreEntity(entityType))
            this.coreEntities[entityType]--;
          else if (this.isFileAttachmentEntity(entityType))
            this.fileAttachmentEntities[entityType]--;
          else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
            this.customObjectEntities[entityType]--;
          else if (this.metaDataService.isPrimaryEntity(entityType))
            this.additionalEntities[entityType]--;
        }
        if (this.ruleSchedule(rule.id as string)) {
          this.scheduleDataService.deleteSchedule(rule.id as string).subscribe(() => {
          }, error => console.warn('Error deleting the rule schedule: ', error));
        }
        if (ruleInChain) {
          this.deleteRuleInChain(rule).subscribe(() => {
          }, error => console.warn('Error deleting rule from chain: ', error));
        }
        this.onRuleDeleted(rule.id as string);
        this.populateDataRules();
        this.newRuleButtonsDisabled = false;
        },
        error => {
          this.errorNotificationService.error('Failed to delete rule', error);
        });
    });
  }

  openScheduled(i: number): void {

    // Allow the user to open a scheduled rule
    if ((this.amountOfScheduledRules >= this.adminService.maxNumberOfAutomatedRules) &&
      this.adminService.maxNumberOfAutomatedRules > 0 && !this.ruleSchedule(this.dataRules[i].ruleData.id as string)) {
      const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
        width: '45vw',
        height: 'auto',
        autoFocus: true,
        data: {
          titleIcon: 'warning',
          title: `Maximum ${this.adminService.maxNumberOfAutomatedRules} automated DataRules available reached`,
          messageIcon: 'alarm',
          message: 'You have reached the maximum number of automated DataRules available, please contact support@kyloepartners.com',
          hideCancelButton: true,
          confirmButtonText: 'OK'
        }
      });
      dialogRef.afterClosed().subscribe(data => {
        if (!data) {
          return;
        }
      });
    } else {
      this.dataRules[i].viewState.scheduleOpen = true;
    }
  }

  duplicateRule(index: number): void {
    const clone = RuleData.clone(this.dataRules[index].ruleData);
    clone.id = 0;
    clone.name = `COPY OF ${clone.name}`;
    this.dataRules.push({ruleData: clone, viewState: {expanded: true, dirty: true}});
    this.newRuleButtonsDisabled = true;
    setTimeout(() => {
      const ruleComponents = this.rules.toArray();
      const clonedRuleComponent = ruleComponents[ruleComponents.length - 1];
      clonedRuleComponent.form.markAsDirty();
      clonedRuleComponent.dataRule.viewState.dirty = true;
      this.navigateToDataRules(this.selectedEntity, [...this.routeParamRuleIds, 0]);
    }, 1000);
  }

  viewExamplesForRule(index: number, ruleID: DataRuleId): void {
    if (this.isRuleEnrichBlocked(this.dataRules[index]))
      return;
    if (!this.dataRules[index].viewState.expanded)
      this.onRuleClicked(ruleID);
    this.currentRuleExamples = this.dataRules[index].ruleData;
  }

  ruleHasEnrichAction(rule: IDataRule): boolean {
    const steps = rule.ruleData.action?.config?.steps ?? [];
    return steps.some(step => step.type === ActionsOperators.ENRICH);
  }

  isRuleEnrichBlocked(rule: IDataRule): boolean {
    return this.enrichCreditsExhausted && this.ruleHasEnrichAction(rule);
  }

  saveSchedule(scheduleRule: ScheduleData, i: number): void {
    this.dataRules[i].viewState.scheduleSaving = true;
    this.dataRules[i].viewState.scheduleOpen = false;
    this.scheduleDataService.saveSchedule(scheduleRule).subscribe(() => {
        this.dataRules[i].viewState.scheduleSaving = false;
        this.populateScheduleList();
    });
  }

  deleteSchedule(i: number): void {
    this.dataRules[i].viewState.scheduleSaving = true;
    this.deletingSchedule = true;
    this.scheduleDataService.deleteSchedule(this.dataRules[i].ruleData.id as string).subscribe(() => {
      this.dataRules[i].viewState.scheduleOpen = false;
      this.dataRules[i].viewState.scheduleSaving = false;
      this.deletingSchedule = false;
    });
  }

  private errorHandler(): void {
    this.errorNotificationService.errors.subscribe(err => {
      console.error('KPDR:', err);
      this.dialog.open(ErrorModalComponent, {height: 'auto', data: {title: err.title, errorMessage: err.message, cause: err.cause}});
    });
  }

  renderIcon(entity: string): string {
    return this.utilsService.renderEntityIcon(entity);
  }

  private isCoreEntity(entityType): boolean {
    return this.coreOrder.indexOf(entityType) > -1;
  }

  private isFileAttachmentEntity(entityType): boolean {
    return entityType.includes('FileAttachment');
  }

  shiftLeadButton(a: KeyValue<string, number>, b: KeyValue<string, number>): number {
    const ai = this.coreOrder.indexOf(a.key);
    const bi = this.coreOrder.indexOf(b.key);
    return ai - bi;
  }

  private onRuleSaved(eventData: ISavedDataRuleEventData): void {
    const {savedId, unsavedId} = eventData;
    const routeParamRuleIds = this.routeParamRuleIds;
    const routeParamEntity = this.routeParamEntity;
    let newIds;
    if (`${unsavedId}` !== `${savedId}`) {
      newIds = routeParamRuleIds.map(id => `${id}` === '0' ? savedId : id);
    } else {
      newIds = routeParamRuleIds;
    }

    this.ruleChainsDataService.ruleUpdated(savedId).subscribe();

    const rule = this.dataRulesDataService.getRule(savedId);
    const entityType = rule.source.config.entity;

    if (unsavedId === 0) {  // Only increment the rule count if it is a new rule being saved
      if (this.isCoreEntity(entityType))
        this.coreEntities[entityType]++;
      else if (this.isFileAttachmentEntity(entityType))
        this.fileAttachmentEntities[entityType]++;
      else if (this.metaDataService.isCustomObjectPrimaryEntity(entityType))
        this.customObjectEntities[entityType]++;
      else if (this.metaDataService.isPrimaryEntity(entityType))
        this.additionalEntities[entityType]++;
    }
    this.newRuleButtonsDisabled = false;
    this.navigateToDataRules(routeParamEntity, newIds);
  }

  private onRuleDeleted(deletedId: DataRuleId): void {
    const routeParamRuleIds = this.routeParamRuleIds;
    const routeParamEntity = this.routeParamEntity;
    const newIds = routeParamRuleIds.reduce(
      (accum, id) => this.utilsService.compareInternalDataRuleIds(id, deletedId) ? accum : [...accum, id], []
    );

    this.navigateToDataRules(routeParamEntity, newIds);
  }

  onEntityClicked(entity: AllPrimaryEntitiesNames): void {
    if (this.routeParamEntity !== entity) {
      if (this.dataRules.some(rule => rule.viewState.dirty)) {
        if (!window.confirm('Unsaved changes. Do you want to continue?'))
          return;
      }
      this.navigateToDataRules(entity);
    }
  }

  onRuleClicked(id: DataRuleId) {
    this.currentRuleExamples = null;
    let isSelectedRuleUnsaved = false;
    const isSelectedRule = this.routeParamRuleIds.findIndex(routeId => this.utilsService.compareInternalDataRuleIds(routeId, id)) > -1;
    let newIDs: DataRuleId[];
    if (isSelectedRule) {
      isSelectedRuleUnsaved = this.dataRules.some(
        rule => rule.viewState.dirty && this.utilsService.compareInternalDataRuleIds(rule.ruleData.id, id)
      );
      newIDs = this.routeParamRuleIds.filter(routeId => `${routeId}` !== `${id}`);
    } else {
      newIDs = [...this.routeParamRuleIds, id];
    }

    if (isSelectedRuleUnsaved) {
      if (!window.confirm('Unsaved changes. Do you want to continue?'))
        return;
      if (this.utilsService.compareInternalDataRuleIds(id, 0)) {
        this.populateDataRules();
        this.newRuleButtonsDisabled = false;
      }
    }

    this.navigateToDataRules(this.routeParamEntity, newIDs);
  }

  shouldRuleChainInfoClose(): boolean {
    return true;
  }

  onRouteChange(previousParams, newParams): void {

    // Getting the entity and rule query params
    // 'entity' param expected to be undefined OR 'EntityName'-like string
    const routeEntityParam = newParams['entity'];
    this.routeParamEntity = routeEntityParam;

    // 'rule' param expected to be undefined OR 'id,id,id...'-like string
    const routeRuleParam = newParams['rule'];
    this.routeParamRuleIds = routeRuleParam ? routeRuleParam.split(',') : [];

    // 'chain' param expected to be undefined OR '' OR 'id,id,id...'-like string
    const routeChainParam = newParams['chain'];
    const areChainsOpened = typeof routeChainParam === 'string';

    // Checking if we have previous params, if so, getting the previous entity
    const previousEntity: string = previousParams ? previousParams['entity'] : null;
    const entityChanged = (routeEntityParam !== previousEntity) || (previousEntity === null);

    // Checking if the entity selected is a valid entity, if not, redirecting to candidate
    if (!AllPrimaryEntitiesTypes.isContains(this.routeParamEntity) || !this.metaDataService.isPrimaryEntity(this.routeParamEntity)) {
      this.navigateToDataRules();
    }

    // Checking the case if chainedRules disabled
    if (!this.chainedRulesEnabled && areChainsOpened) {
      this.navigateToDataRules(this.routeParamEntity, this.routeParamRuleIds);
    }

    if (entityChanged) {
      this.onEntitySelected(this.routeParamEntity);
    }

    if (this.routeParamRuleIds.length) {
      // 'rule' parameter is higher priority than 'chain'
      if (areChainsOpened) {
        this.navigateToDataRules(this.routeParamEntity, this.routeParamRuleIds);
        return;
      }
      this.proceedToNavigatedRules();
    } else {
      this.dataRules.forEach(rule => rule.viewState.expanded = false);
    }
  }

  proceedToNavigatedRules(): void {
    if (this.routeParamEntity) {
      const isMissingRule = this.routeParamRuleIds.findIndex(ruleId =>
        this.dataRules.findIndex(entityRule => this.utilsService.compareInternalDataRuleIds(entityRule.ruleData.id, ruleId)) === -1
      ) > -1;
      if (isMissingRule) {
        // navigate to specified entity if any of specified rules are not related to that entity
        this.navigateToDataRules(this.routeParamEntity);
      }
    }
    // 0 milliseconds timeout required to await Angular asynchronous stuff
    setTimeout(() => {
      // collapse required-to rules if they are expanded
      this.dataRules.forEach(rule => {
        if (rule.viewState.expanded) {
          if (this.routeParamRuleIds.findIndex(
            paramRuleId => this.utilsService.compareInternalDataRuleIds(paramRuleId, rule.ruleData.id)
          ) === -1) {
            rule.viewState.expanded = false;
          }
        }
      });

      // expand required-to rules if they are collapsed
      this.routeParamRuleIds
        .map(id => this.dataRules.find(rule => this.utilsService.compareInternalDataRuleIds(rule.ruleData.id, id)))
        .forEach(rule => {
          rule.viewState.expanded = true;
        });

      // scrolling into the last rule from selected
      const ruleIdToScrollInto = this.routeParamRuleIds[this.routeParamRuleIds.length - 1];
      const ruleComponent = this.rules.find(
        rule => this.utilsService.compareInternalDataRuleIds(rule.dataRule.ruleData.id, ruleIdToScrollInto)
      );
      if (ruleComponent) {
        // 500 milliseconds timeout required to await Angular to finish rendering this.rules QueryList ref components
        setTimeout(() => {
          ruleComponent.ruleReference.nativeElement.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'center'});
        }, 500);
      }
    }, 0);
  }

  protected navigateToDataRules(entity?: AllPrimaryEntitiesNames, rules?: DataRuleId[]): void {
    if (!entity || !AllPrimaryEntitiesTypes.isContains(entity) || !this.metaDataService.isPrimaryEntity(entity)) {
      console.log('Invalid entity type: ', entity);
      this.router.navigate(['datarules'], {queryParams: {entity: DefaultEntities.DEFAULT_DATARULES_ENTITY}});
      return;
    }
    const rulesParam = rules && rules.length > 0 ? rules.join(',') : undefined;
    const chainsParam = undefined;
    this.router.navigate(['datarules'], {queryParams: {entity, rule: rulesParam, chain: chainsParam}});
  }

  private navigateToChains(chains?: DataRuleId[]): void {
    const entityParam = this.routeParamEntity;
    const rulesParam = undefined;
    const chainsParam = chains && chains.length > 0 ? chains.join(',') : '';
    this.router.navigate(['datarules'], {queryParams: {entity: entityParam, rule: rulesParam, chain: chainsParam}});
  }

  deleteRuleInChain(rule: RuleData): Observable<void> {
    let chain: ChainData = this.ruleChainsDataService.getChainByRuleId(rule.id);
    this.ruleChainsDataService.removeRuleFromChain(chain.id, chain.ruleIds.findIndex(chainedRuleId => chainedRuleId === rule.id));
    chain = this.ruleChainsDataService.getChain(chain.id);
    return this.ruleChainsDataService.saveChain(chain).pipe(map(() => {
      if (chain.ruleIds.length <= 0) {
        const dialogRef = this.dialog.open(DataRulesConfirmDialogComponent, {
          width: '45vw',
          height: 'auto',
          autoFocus: true,
          data: {
            titleIcon: 'bhi-delete-o',
            title: `This rule was the last remaining rule in chain ${chain.name}. Do you you want to delete the chain?`,
            messageIcon: 'bhi-search',
            message: chain.name
          }
        });
        dialogRef.afterClosed().subscribe(data => {
          if (!data) {
            return;
          }
          this.ruleChainsDataService.deleteChain(chain.id).subscribe();
        });
      }
    }));
  }
}
